import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

export interface ChannelPayload {
    url: string;
    name?: string;
    data?: string;
}

export interface ChatContextValue {
    open: boolean;
    minimized: boolean;
    channel: ChannelPayload | null;
    openWithChannel: (channel: ChannelPayload | string) => void;
    close: () => void;
    minimize: () => void;
    restore: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [channel, setChannel] = useState<ChannelPayload | null>(null);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    useEffect(() => {
        if (!loggedIn) {
            setOpen(false);
            setMinimized(false);
            setChannel(null);
        }
    }, [loggedIn]);

    const openWithChannel = useCallback((ch: ChannelPayload | string) => {
        const payload: ChannelPayload | null =
            typeof ch === 'string' ? { url: ch } : ch ?? null;
        if (!payload || !payload.url) return;
        setChannel(payload);
        setMinimized(false);
        setOpen(true);
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        setChannel(null);
    }, []);

    const minimize = useCallback(() => {
        setMinimized(true);
        setOpen(false);
    }, []);

    const restore = useCallback(() => {
        setMinimized(false);
        setOpen(true);
    }, []);

    return (
        <ChatContext.Provider
            value={{ open, minimized, channel, openWithChannel, close, minimize, restore }}
        >
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = (): ChatContextValue => {
    const ctx = useContext(ChatContext);
    if (!ctx) throw new Error('useChat must be used inside ChatProvider');
    return ctx;
};
